/* eslint-disable check-react-ssr-usage/guard-browser-globals -- Client-only instrumentation; non-content 3P apps are fetched and rendered on the client, never SSRed. */

/**
 * Instrumentation for third-party (3P) apps rendered OUTSIDE of page content
 * (e.g. byline, page banner, context menu, content tools, web panels).
 *
 * Unlike content macros/extensions (which are indexed from the ADF document by
 * `ExtrensionIndexProvider`), these apps are fetched client-side via GraphQL
 * hooks (`useWebItemLocation` / `useExtensionList`) and are therefore invisible
 * to the content-based telemetry.
 *
 * Each non-content surface calls `recordNonContentAppKeys` when its apps resolve.
 * The collected keys are stashed on a dedicated window global and later folded
 * into the page-load metric's `connectAppKeys` attribute (React UFO
 * `extra_properties`) by `pageLoadEventPlugin`, so they land in the same RUM
 * dataset as content app keys.
 */

declare global {
	interface Window {
		__NON_CONTENT_APP_KEYS__?: Set<string>;
	}
}

/**
 * The surface a non-content 3P app is rendered on. Used to prefix recorded app
 * keys so telemetry can tell where each app came from.
 */
export type NonContentAppSource =
	| 'byline'
	| 'page-banner'
	| 'background-script'
	| 'content-tools'
	| 'web-panel'
	| 'web-item';

/**
 * Delimiter separating the source from the app key in a recorded entry.
 * A double colon is used so it doesn't clash with the single colons already
 * present in connect `completeKey`s (`plugin:xyz`) or Forge ARIs.
 */
export const NON_CONTENT_APP_KEY_DELIMITER = '::';

/**
 * Record one or more non-content 3P app keys for the current page load, tagged
 * with the surface (`source`) they were rendered on.
 *
 * - Connect apps: pass the web item `completeKey`.
 * - Forge apps: pass the extension `id` (ARI).
 *
 * Each key is stored as `` `${source}::${key}` `` so downstream telemetry can
 * attribute the app to its surface (e.g. `byline::plugin:my-app`).
 *
 * Keys are stored in a `Set`, so recording is naturally de-duplicated and cheap
 * to call repeatedly (e.g. from imperative render-phase call sites). Falsy/empty
 * values are ignored.
 */
export const recordNonContentAppKeys = (
	source: NonContentAppSource,
	appKeys: (string | null | undefined)[],
): void => {
	if (typeof window === 'undefined') {
		return;
	}

	const keys = window.__NON_CONTENT_APP_KEYS__ ?? new Set<string>();
	for (const key of appKeys) {
		if (key) {
			keys.add(`${source}${NON_CONTENT_APP_KEY_DELIMITER}${key}`);
		}
	}
	window.__NON_CONTENT_APP_KEYS__ = keys;
};

/**
 * Read the recorded non-content 3P app keys for the current page load.
 * Returns an empty `Set` when none have been recorded.
 */
export const getNonContentAppKeys = (): Set<string> =>
	typeof window === 'undefined' ? new Set() : (window.__NON_CONTENT_APP_KEYS__ ?? new Set());

/**
 * Clear recorded non-content 3P app keys. Intended for page/route transitions
 * (and test cleanup) so keys don't leak across page loads.
 */
export const resetNonContentAppKeys = (): void => {
	if (typeof window === 'undefined') {
		return;
	}
	window.__NON_CONTENT_APP_KEYS__ = new Set();
};
